import React, { useState } from "react";
import { ChevronDown, ChevronUp, HelpCircle } from "lucide-react";

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0); // První otázka bude rovnou rozbalená

  const faqs = [
    {
      question: "Je přestavba legální pro provoz na pozemních komunikacích?",
      answer: (
        <>
          <p className="mb-2">
            <strong>Ano, všechny naše sady jsou 100% legální</strong> a můžete s nimi bez obav na silnice i cyklostezky. 
          </p>
          <p>
            Podle aktuální <strong>vyhlášky č. 153/2023 Sb.</strong> může být na kole nainstalován dodatečný elektrický pohon o výkonu <strong>až 1 kW (1000 W)</strong> za předpokladu, že je asistence motoru softwarově omezena na maximální rychlost <strong>25 km/h</strong>. Všechny námi prodávané motory tento omezovač plně respektují. Z vašeho kola se tak po přestavbě stává standardní elektrokolo (EPAC), které nevyžaduje řidičský průkaz, pojištění ani žádnou registraci.
          </p>
        </>
      ),
    },
    {
      question: "Zvládnu si sadu nainstalovat sám doma, nebo musím do servisu?",
      answer: (
        <>
          <p className="mb-2">
            Přestavbu{" "}
            <strong>
              zvládne průměrně zručný člověk v domácích podmínkách
            </strong>{" "}
            zhruba za 2 až 4 hodiny. Není potřeba žádné složité řezání nebo
            sváření rámu – motor se pouze zasune na místo původního středového
            složení a kabely se propojí pomocí nacvakávacích voděodolných
            konektorů (nelze je zapojit špatně).
          </p>
          <p>
            Budete potřebovat jen základní nářadí na kolo (stahovák na kliky a
            klíč na středové složení). Ke každé sadě navíc poskytujeme detailní
            návod. Pokud si na to přesto netroufáte, můžete využít naší služby
            odborné montáže.
          </p>
        </>
      ),
    },
    {
      question: "Jaký je reálný dojezd na jedno nabití a co ho ovlivňuje?",
      answer: (
        <>
          <p className="mb-2">
            Dojezd je u elektrokol variabilní, proto ho počítáme podle jasně daných fyzikálních parametrů. Níže uvedené hodnoty platí pro <strong>jezdce o váze 85 kg, na víceméně rovinatém terénu, při rychlosti 25 km/h a na 1. stupeň přípomoci</strong> (systém má 5 módů, kde 5 pomáhá nejvíce).
          </p>
          <p className="mb-4">
            Při této klidné jízdě má motor průměrnou spotřebu zhruba <strong>100 až 200 W</strong>. Z kapacity baterie (Wh) se tak dá velmi přesně odvodit dojezd:
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-4 text-slate-700">
            <li>
              <strong>Baterie 468 Wh (36V 13Ah):</strong> Reálný dojezd cca <strong>70–115 km</strong>. Ideální pro běžné odpolední výlety.
            </li>
            <li>
              <strong>Baterie 630 Wh (36V 17.5Ah):</strong> Reálný dojezd cca <strong>100–150 km</strong>. Skvělé pro celodenní ježdění bez stresu z vybití.
            </li>
            <li>
              <strong>Baterie 1040 Wh (52V 20Ah):</strong> Reálný dojezd cca <strong>170–250 km</strong>. Pro extrémní trasy a jezdce, kteří nechtějí dělat žádné kompromisy.
            </li>
          </ul>
          <p className="text-sm text-slate-500">
            * Pokud zvolíte vyšší stupeň asistence (3-5), pojedete do prudkých kopců nebo přidáte zátěž, spotřeba motoru stoupne a celkový dojezd bude úměrně tomu nižší.
          </p>
        </>
      ),
    },
    {
      question: "Vejde se středový motor opravdu na moje kolo?",
      answer: (
        <>
          <p className="mb-2">
            Naše středové motory jsou navrženy tak, aby pasovaly na{" "}
            <strong>99 % běžných jízdních kol</strong>.
          </p>
          <p>
            Rozhodujícím parametrem je středové složení kola. Motor je
            kompatibilní se standardním středem typu{" "}
            <strong>BSA se závitem o šířce 68 až 73 mm</strong>, což má naprostá
            většina kol na trhu. Pokud máte moderní karbonový rám s "PressFit"
            středem (bez závitu) nebo tzv. Fatbike s velmi širokým středem,
            instalace může vyžadovat speciální adaptér nebo dlouhou osu motoru.
            Pokud si nejste jistí, vyfoťte nám své kolo na e-mail a my vám
            obratem potvrdíme kompatibilitu.
          </p>
        </>
      ),
    },
    {
      question: "Jak se mám správně starat o baterii, aby mi vydržela roky?",
      answer: (
        <>
          <p className="mb-2">
            Moderní Li-Ion baterie nemají paměťový efekt, takže je{" "}
            <strong>
              můžete (a měli byste) nabíjet klidně i po krátké jízdě
            </strong>
            . Pro maximální životnost dodržujte tato 3 zlatá pravidla:
          </p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>
              <strong>Nenechávejte baterii dlouho vybitou na 0 %.</strong> Pokud
              dojedete a baterie je prázdná, dejte ji co nejdříve na nabíječku.
            </li>
            <li>
              <strong>Zazimování:</strong> Pokud v zimě nejezdíte, uložte
              baterii doma v pokojové teplotě (nikdy v mrazivé garáži) nabitou
              zhruba na 50 až 70 % a jednou za 2 měsíce ji nechte chvíli na
              nabíječce oživit.
            </li>
            <li>
              <strong>Vyhněte se extrémům:</strong> Nevystavujte baterii přímému
              letnímu slunci po dlouhé hodiny a k nabíjení používejte výhradně
              originální nabíječku, kterou od nás dostanete v sadě.
            </li>
          </ol>
        </>
      ),
    },
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-16 md:py-24">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-extrabold text-[#2a3b4c] flex items-center justify-center gap-3">
          <HelpCircle className="w-8 h-8 text-green-500" />
          Často kladené otázky
        </h2>
        <p className="text-slate-500 mt-4 text-lg">
          Vše, co potřebujete vědět před pořízením přestavbové sady.
        </p>
      </div>

      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div
            key={index}
            className={`bg-white border rounded-2xl transition-all duration-300 overflow-hidden ${
              openIndex === index
                ? "border-green-300 shadow-md ring-1 ring-green-100"
                : "border-slate-200 hover:border-slate-300"
            }`}
          >
            <button
              onClick={() => toggleFAQ(index)}
              className="w-full text-left px-6 py-5 flex items-center justify-between focus:outline-none"
            >
              <h3
                className={`font-bold text-lg md:text-xl pr-8 ${
                  openIndex === index ? "text-green-700" : "text-[#2a3b4c]"
                }`}
              >
                {faq.question}
              </h3>
              <div
                className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                  openIndex === index
                    ? "bg-green-100 text-green-600"
                    : "bg-slate-50 text-slate-400"
                }`}
              >
                {openIndex === index ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </div>
            </button>

            <div
              className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${
                openIndex === index
                  ? "max-h-[800px] opacity-100 pb-6"
                  : "max-h-0 opacity-0 pb-0"
              }`}
            >
              <div className="text-slate-600 text-base leading-relaxed border-t border-slate-100 pt-4 mt-2">
                {faq.answer}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FAQSection;